import { FC, useEffect, useState } from 'react'

import {
	Box,
	Checkbox,
	ColumnLayout,
	FormField,
	Input,
	SpaceBetween
} from '@cloudscape-design/components'

import IpAddressAllocationEditor, {
	type IpAddressAllocationRow
} from '../IpAddressAllocationEditor'
import { ACCOUNT_BASE_NETWORK_FIELDS } from '../../constants/accountNetworkRequestFields'
import {
	getCommaSeparatedCidrListError,
	getPrivateTgwSubnetsCidrListError,
	getSingleCidrError,
	PRIVATE_TGW_SUBNETS_CIDR_PLACEHOLDER,
	VPC_CIDR_BLOCK_PLACEHOLDER
} from '../../helper/cidrValidation'
import {
	getEksPodSecondaryCidrError,
	getPrivateHostedZoneNameError,
	ipAllocationsHaveInput,
	isAccountEksWorkload,
	PRIVATE_HOSTED_ZONE_NAME_PLACEHOLDER,
	validateAccountNetworkDetails
} from '../../helper/accountNetworkValidation'
import {
	defaultVpcAvailabilityZonesForAccountType,
	withThirdAvailabilityZone
} from '../../helper/accountRequestNetworkSummary'
import { normalizeIpAllocationsFromForm } from '../../helper/ipAllocationValidation'

type AccountNetworkDetailsEditorProps = {
	formValues: Record<string, unknown>
	setFormValues: (fieldName: string, value: unknown) => void
	isProduction: boolean
	accountType?: string
	onValidityChange?: (isValid: boolean) => void
	/** Bump when parent replaces form values from account/STG prefill (not on every field edit). */
	networkPrefillGeneration?: number
	/**
	 * Show the "Add third availability zone" checkbox. Enabled for the STG approval flow
	 * (which starts at two zones) and Production account approval (which starts at three);
	 * non-prod/sandbox account approval stays fixed at two.
	 */
	allowThirdAzToggle?: boolean
}

const AccountNetworkDetailsEditor: FC<AccountNetworkDetailsEditorProps> = ({
	formValues,
	setFormValues,
	isProduction,
	accountType,
	onValidityChange,
	networkPrefillGeneration = 0,
	allowThirdAzToggle = false
}) => {
	const accountWorkloadType = String(formValues.accountWorkloadType ?? 'Non-EKS')
	const ipAllocationsRequired = isAccountEksWorkload(accountWorkloadType)

	const [ipRows, setIpRows] = useState<IpAddressAllocationRow[]>(() =>
		normalizeIpAllocationsFromForm(
			formValues.ip_address_allocations,
			isProduction,
			ipAllocationsRequired
		) as IpAddressAllocationRow[]
	)

	useEffect(() => {
		setIpRows(
			normalizeIpAllocationsFromForm(
				formValues.ip_address_allocations,
				isProduction,
				ipAllocationsRequired
			) as IpAddressAllocationRow[]
		)
		// Do not depend on formValues.ip_address_allocations: that field is written back from
		// ipRows and would clear/rebuild rows on every Add / keystroke (esp. Non-EKS).
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ipAllocationsRequired, isProduction, networkPrefillGeneration])

	/**
	 * `isProduction` here means "allocations carry AZ C", which the parents now derive from
	 * vpcAvailabilityZones rather than from the account type alone. Toggling rewrites that
	 * list; the parents recompute and feed the new value straight back down.
	 */
	const thirdAzEnabled = isProduction

	const handleThirdAzToggle = (enabled: boolean) => {
		setFormValues(
			'vpcAvailabilityZones',
			withThirdAvailabilityZone(formValues.vpcAvailabilityZones, enabled)
		)
		if (!enabled) {
			// Never submit a cidrAzC the reviewer can no longer see.
			setIpRows((prev) => prev.map((row) => ({ ...row, cidrAzC: '' })))
		}
	}

	const addSecondary = Boolean(formValues.addSecondaryCidrBlock)
	const secondaryCidr = String(formValues.secondaryCidrBlock ?? '')
	const addPublic = Boolean(formValues.addPublicCidrBlock)
	const publicCidr = String(formValues.publicCidrBlock ?? '')

	// Mirrors validateAccountNetworkDetails: allocations with CIDRs make Private Subnets CIDR
	// optional, so the "fill all fields" error must not fire on it either.
	const privateSubnetsOptional = ipAllocationsHaveInput(ipRows)

	const networkInvalid = ACCOUNT_BASE_NETWORK_FIELDS.some(
		(f) =>
			!(f.key === 'privateSubnetsCidr' && privateSubnetsOptional) &&
			!String(formValues[f.key] ?? '').trim()
	)

	const secondaryInvalid =
		addSecondary &&
		(!secondaryCidr.trim() || Boolean(getCommaSeparatedCidrListError(secondaryCidr)))

	// Surfaced even when the checkbox is unticked — that is itself the failure mode for EKS.
	const eksPodCidrError = getEksPodSecondaryCidrError(
		accountWorkloadType,
		secondaryCidr
	)

	const publicInvalid =
		addPublic && (!publicCidr.trim() || Boolean(getCommaSeparatedCidrListError(publicCidr)))

	const multiCidrFieldKeys = new Set(['privateSubnetsCidr', 'tgwSubnetsCidr'])

	const getMultiCidrFieldError = (key: string): string | undefined => {
		const value = String(formValues[key] ?? '').trim()
		if (!value) return undefined
		return getPrivateTgwSubnetsCidrListError(value) ?? undefined
	}

	const getFieldError = (key: string): string | undefined => {
		if (multiCidrFieldKeys.has(key)) {
			return getMultiCidrFieldError(key)
		}
		if (key === 'vpcCidrBlock') {
			const value = String(formValues[key] ?? '').trim()
			if (!value) return undefined
			return getSingleCidrError(value) ?? undefined
		}
		if (key === 'privateHostedZoneName') {
			const value = String(formValues[key] ?? '').trim()
			if (!value) return undefined
			return getPrivateHostedZoneNameError(value) ?? undefined
		}
		return undefined
	}

	const getFieldDescription = (_key: string): string | undefined => undefined

	const getFieldPlaceholder = (key: string, label: string): string => {
		if (multiCidrFieldKeys.has(key)) return PRIVATE_TGW_SUBNETS_CIDR_PLACEHOLDER
		if (key === 'vpcCidrBlock') return VPC_CIDR_BLOCK_PLACEHOLDER
		if (key === 'vpcName') return 'e.g. isg-myplatform-dv-aws-vpc-1'
		if (key === 'privateHostedZoneName') return PRIVATE_HOSTED_ZONE_NAME_PLACEHOLDER
		return `Enter ${label}`
	}

	const stepValid = validateAccountNetworkDetails(
		{
			...formValues,
			accountWorkloadType,
			ip_address_allocations: ipRows
		},
		isProduction,
		ipRows
	)

	useEffect(() => {
		onValidityChange?.(stepValid)
	}, [onValidityChange, stepValid])

	useEffect(() => {
		setFormValues(
			'ip_address_allocations',
			ipRows.map(({ id, label, cidrAzA, cidrAzB, cidrAzC }) =>
				isProduction
					? { id, label, cidrAzA, cidrAzB, cidrAzC: cidrAzC ?? '' }
					: { id, label, cidrAzA, cidrAzB }
			)
		)
	}, [ipRows, isProduction, setFormValues])

	useEffect(() => {
		const cur = String(formValues.vpcAvailabilityZones ?? '').trim()
		if (cur) {
			return
		}
		setFormValues(
			'vpcAvailabilityZones',
			defaultVpcAvailabilityZonesForAccountType(accountType ?? 'Sandbox')
		)
	}, [accountType, formValues.vpcAvailabilityZones, setFormValues])

	return (
		<SpaceBetween direction="vertical" size="l">
			<ColumnLayout columns={2} variant="default">
				{ACCOUNT_BASE_NETWORK_FIELDS.map(({ key, label }) => (
					<FormField
						key={key}
						label={label}
						description={getFieldDescription(key)}
						errorText={getFieldError(key)}
					>
						<Input
							name={`account-network-${key}`}
							autoComplete={false}
							disableBrowserAutocorrect
							value={String(formValues[key] ?? '')}
							placeholder={getFieldPlaceholder(key, label)}
							onChange={({ detail }) => setFormValues(key, detail.value)}
						/>
					</FormField>
				))}
			</ColumnLayout>

			<Checkbox
				checked={addSecondary}
				onChange={({ detail }) => {
					setFormValues('addSecondaryCidrBlock', detail.checked)
					if (!detail.checked) {
						setFormValues('secondaryCidrBlock', '')
					}
				}}
			>
				Add Secondary CIDR Block
			</Checkbox>
			{!addSecondary && eksPodCidrError && (
				<Box color="text-status-error" fontSize="body-s">
					{eksPodCidrError}
				</Box>
			)}
			{addSecondary && (
				<FormField
					label="Secondary CIDR Block"
					errorText={
						secondaryInvalid && secondaryCidr.trim()
							? getCommaSeparatedCidrListError(secondaryCidr) ?? undefined
							: secondaryInvalid
								? 'Enter at least one valid CIDR'
								: eksPodCidrError
					}
				>
					<Input
						name="account-network-secondaryCidrBlock"
						autoComplete={false}
						disableBrowserAutocorrect
						value={secondaryCidr}
						placeholder="e.g. 10.1.0.0/16 or 10.1.0.0/16, 10.2.0.0/16"
						onChange={({ detail }) =>
							setFormValues('secondaryCidrBlock', detail.value)
						}
					/>
				</FormField>
			)}

			<Checkbox
				checked={addPublic}
				onChange={({ detail }) => {
					setFormValues('addPublicCidrBlock', detail.checked)
					if (!detail.checked) {
						setFormValues('publicCidrBlock', '')
					}
				}}
			>
				Add Public CIDR
			</Checkbox>
			{addPublic && (
				<FormField
					label="Public CIDR"
					errorText={
						publicInvalid && publicCidr.trim()
							? getCommaSeparatedCidrListError(publicCidr) ?? undefined
							: publicInvalid
								? 'Enter at least one valid CIDR'
								: undefined
					}
				>
					<Input
						name="account-network-publicCidrBlock"
						autoComplete={false}
						disableBrowserAutocorrect
						value={publicCidr}
						placeholder="e.g. 10.1.0.0/16 or 10.1.0.0/16, 10.2.0.0/16"
						onChange={({ detail }) => setFormValues('publicCidrBlock', detail.value)}
					/>
				</FormField>
			)}

			{allowThirdAzToggle && (
				<Checkbox
					checked={thirdAzEnabled}
					onChange={({ detail }) => handleThirdAzToggle(detail.checked)}
				>
					Add Third Availability Zone
				</Checkbox>
			)}

			<IpAddressAllocationEditor
				allocations={ipRows}
				onChange={setIpRows}
				isProduction={isProduction}
				errorText={
					!stepValid && !networkInvalid
						? ipAllocationsRequired
							? 'Fill all required CIDR fields with valid values.'
							: 'Complete each row you started, or clear partial entries.'
						: undefined
				}
			/>

			{networkInvalid && (
				<Box color="text-status-error" fontSize="body-s">
					Fill all network detail fields.
				</Box>
			)}
		</SpaceBetween>
	)
}

export default AccountNetworkDetailsEditor
